import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TEST_IDS } from '../../../config/utils/constants';
import { nowDateInUtc } from '../../../config/utils/dateHelpers';
import {
  canBeMarkedAsDeliveryNote,
  isDocTypeDeliveryNote,
  isQuantityReceiptForm,
} from '../../../services/utils/myDataUtils';
import { DocDate } from './DocDate';
import { DocDeliverToCity } from './DocDeliverToCity';
import { DocDeliverToPostalCode } from './DocDeliverToPostalCode';
import { DocDelivery } from './DocDelivery';
import { DocSimpleInput } from './DocSimpleInput';

interface Props {
  shippingField: any;
  shipFrom: any;
  shipTo: any;
  dueDate: any;
  shipDate: any;
  shipToCity: any;
  shipToPostalCode: any;
  formik: any;
}

export const DocPaymentDelivery = ({
  shippingField,
  shipFrom,
  shipTo,
  shipToCity,
  shipToPostalCode,
  dueDate,
  shipDate,
  formik,
}: Props) => {
  const { t } = useTranslation();
  const setFieldValue = formik?.setFieldValue;
  const minDate = useMemo(() => dayjs(formik.values?.documentDate || ''), [formik.values?.documentDate]);
  const documentType = useMemo(
    () => formik?.values?.documentTypeId?.object?.aadeCode,
    [formik?.values?.documentTypeId?.object?.aadeCode],
  );
  const shipDateValue = useMemo(() => formik?.values?.shipDate, [formik?.values?.shipDate]);
  const isMarkedAsDeliveryNote = useMemo(() => formik?.values?.isDeliveryNote, [formik?.values?.isDeliveryNote]);
  const isDateRequired = useMemo(
    () => (isDocTypeDeliveryNote(documentType) || isMarkedAsDeliveryNote) && !isQuantityReceiptForm(documentType),
    [documentType, isMarkedAsDeliveryNote],
  );
  // Whether `isDeliveryNote` could still get programmatically flipped to `true` after this render
  // by `DeliveryNoteToggle` (a sibling component, reacting to the same `formik` independently) -
  // it recommends enabling on every clone and defaults a fresh doc to it too, except for doc types
  // in `DOC_TYPE_DELIVERY_NOTE_DISABLED_BY_DEFAULT` (5.1/5.2), which stay off either way -
  // harmless here regardless, this guard only ever skips an unneeded clear on first render.
  const canDocTypeBeDeliveryNote = useMemo(() => canBeMarkedAsDeliveryNote(documentType), [documentType]);
  const isFirstRunRef = useRef(true);

  useEffect(() => {
    const isFirstRun = isFirstRunRef.current;
    isFirstRunRef.current = false;

    if (isDateRequired) {
      if (!shipDateValue) setFieldValue('shipDate', nowDateInUtc());
      return;
    }

    // On the very first render of a new/cloned doc, `DeliveryNoteToggle` may still be in the
    // middle of resolving `isDeliveryNote` to `true` (its own effect, in a completely separate
    // component) - that commit hasn't landed here yet, so `isDateRequired` can transiently read
    // `false` right before it flips. Clearing `shipDate` on that stale read wipes out the
    // prefilled/default value for no reason, right as the form mounts, before the user ever
    // touches it. Skip only that narrow first-render window, and only for doc types where such a
    // flip could actually still happen - a doc type that can never be marked as a delivery note
    // has nothing pending, so it's always safe to clear immediately.
    if (isFirstRun && canDocTypeBeDeliveryNote) return;

    if (shipDateValue) setFieldValue('shipDate', null);
  }, [formik, isDateRequired, setFieldValue, shipDateValue, canDocTypeBeDeliveryNote]);

  const getTransportDate = useCallback(() => {
    if (isDateRequired) {
      const maxDate = minDate.add(30, 'd');
      return (
        <DocDate field={shipDate} formik={formik} title={t('SALE.DELIVERY_DATE')} minDate={minDate} maxDate={maxDate} />
      );
    }
  }, [shipDate, formik, isDateRequired, t, minDate]);

  return (
    <div className="docForm padding-t-24 padding-b-24 padding-l-8 padding-r-8 flex-start-center-row-wrap gap-30-16">
      <DocDelivery field={shippingField} formik={formik} />
      <DocSimpleInput field={shipFrom} formik={formik} testId={TEST_IDS.Invoice.shipFromInput} />
      <DocSimpleInput field={shipTo} formik={formik} testId={TEST_IDS.Invoice.shipToAddressInput} />
      <DocDeliverToCity field={shipToCity} formik={formik} />
      <DocDeliverToPostalCode field={shipToPostalCode} formik={formik} />
      <DocDate field={dueDate} formik={formik} title={t('SALE.DUE_DATE')} minDate={minDate} />
      {getTransportDate()}
    </div>
  );
};
